import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMsal, useAccount } from "@azure/msal-react";
import axios from "axios";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { FaEdit, FaTrashAlt, FaPlus } from "react-icons/fa";
import authConfig from "../../authConfig";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-classic/build/ckeditor";

function MemberNewsEditor({ label }) {
    const { t } = useTranslation();
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});

    const [show, setShow] = useState(false); // main list modal
    const [showEditor, setShowEditor] = useState(false); // add/edit modal
    const [editorMode, setEditorMode] = useState("add"); // "add" | "edit"

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [editItem, setEditItem] = useState({
        id: null,
        title: "",
        content: "",
        isPublished: true
    });

    // delete confirmation state
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteItem, setDeleteItem] = useState({ id: null, title: "" });
    const [deleting, setDeleting] = useState(false);

    const handleShow = () => {
        setError(null);
        setShow(true);
    };
    const handleClose = () => {
        setShow(false);
    };

    // Fetch list when main modal opens
    useEffect(() => {
        if (!show) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const tokenRes = await instance.acquireTokenSilent({
                    scopes: authConfig.api.scopes,
                    account: account
                });
                const res = await axios.get(authConfig.api.urls.getMemberNewsList, {
                    headers: { Authorization: `Bearer ${tokenRes.accessToken}` }
                });
                if (!cancelled) setItems(res.data);
            } catch (err) {
                console.error(err);
                if (!cancelled) setError(t("Error loading news items"));
            }
            if (!cancelled) setLoading(false);
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [show, instance, account, t]);

    const openAdd = () => {
        setError(null);
        setEditItem({
            id: null,
            title: "",
            content: "",
            isPublished: true
        });
        setEditorMode("add");
        setShowEditor(true);
    };

    const openEdit = (item) => {
        setError(null);
        setEditItem({
            id: item.id,
            title: item.title,
            content: item.content || "",
            isPublished: !!item.isPublished
        });
        setEditorMode("edit");
        setShowEditor(true);
    };

    const handleSave = async () => {
        setError(null);
        if (!editItem.title) {
            setError(t("Title is required"));
            return;
        }
        setSaving(true);
        try {
            const tokenRes = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            const client = axios.create({
                headers: { Authorization: `Bearer ${tokenRes.accessToken}` }
            });

            if (editItem.id) {
                // update - server expects full MemberNewsItem
                const payload = {
                    id: editItem.id,
                    title: editItem.title,
                    content: editItem.content,
                    isPublished: editItem.isPublished
                };
                await client.post(authConfig.api.urls.getMemberNewsUpdate, payload);
            } else {
                // create - server expects AddMemberNewsItemRequest
                const payload = {
                    title: editItem.title,
                    content: editItem.content,
                    isPublished: editItem.isPublished
                };
                await client.post(authConfig.api.urls.getMemberNewsCreate, payload);
            }

            // refresh list
            const res = await client.get(authConfig.api.urls.getMemberNewsList);
            setItems(res.data || []);
            // close editor modal after save
            setShowEditor(false);
            // reset form state
            setEditItem({
                id: null,
                title: "",
                content: "",
                isPublished: true
            });
        } catch (err) {
            console.error(err);
            setError(t("Error saving news item"));
        }
        setSaving(false);
    };

    // Open delete confirmation modal (no deletion performed yet)
    const openDeleteConfirm = (item) => {
        setError(null);
        setDeleteItem({ id: item.id, title: item.title });
        setShowDeleteConfirm(true);
    };

    // perform the deletion after user confirms
    const handleDelete = async (id) => {
        if (!id) return;
        setError(null);
        setDeleting(true);
        try {
            const tokenRes = await instance.acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            });
            await axios.get(authConfig.api.urls.getMemberNewsDelete + "/" + id, {
                headers: { Authorization: `Bearer ${tokenRes.accessToken}` }
            });
            // refresh list
            const res = await axios.get(authConfig.api.urls.getMemberNewsList, {
                headers: { Authorization: `Bearer ${tokenRes.accessToken}` }
            });
            setItems(res.data || []);
            // close confirm modal
            setShowDeleteConfirm(false);
            setDeleteItem({ id: null, title: "" });
        } catch (err) {
            console.error(err);
            setError(t("Error deleting news item"));
        }
        setDeleting(false);
    };

    return (
        <>
            <Button variant="success" onClick={handleShow} size="sm">
                {t(label || "Member News")}
            </Button>

            {/* Main modal: list of existing news and actions */}
            <Modal show={show} onHide={handleClose} size="lg" centered autoFocus={false} enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Member News")}</Modal.Title>
                    <div className="ms-auto">
                        <Button variant="primary" size="sm" onClick={openAdd}>
                            <FaPlus /> {t("Add")}
                        </Button>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <h5>{t("Existing News")}</h5>
                    {loading ? (
                        <div>{t("Loading...")}</div>
                    ) : items.length === 0 ? (
                        <div className="text-muted">{t("No news items")}</div>
                    ) : (
                        items.map((it, idx) => (
                            <div style={{ backgroundColor: idx % 2 ? "#EEEEEE" : "#FFFFFF" }}>
                                <Row key={it.id || idx} className="align-items-center mb-2" >
                                    <Col xs={8}>
                                        <div style={{ fontWeight: "600" }}>{it.title}</div>
                                        <div className="text-muted" style={{ fontSize: "0.9em" }}>
                                            {it.publishedDate ? new Date(it.publishedDate).toLocaleString() : ""}
                                            {it.isPublished ? " � " + t("Published") : " � " + t("Draft")}
                                        </div>
                                    </Col>
                                    
                                    <Col xs={2} className="text-end">
                                        <Button variant="success" size="sm" onClick={() => openEdit(it)} title={t("Edit")}>
                                            <FaEdit />
                                        </Button>{" "}
                                        <Button variant="danger" size="sm" onClick={() => openDeleteConfirm(it)} title={t("Delete")}>
                                            <FaTrashAlt />
                                        </Button>
                                    </Col>
                                </Row>
                                <Row >
                                    <Col xs={12}>
                                        <div style={{ maxHeight: 64, overflow: "hidden", whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: it.content || "" }} />
                                    </Col>
                                </Row>
                            </div>
                        ))
                    )}
                    {error ? <div className="text-danger mt-2">{error}</div> : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Close")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete confirmation modal */}
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Confirm Delete")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>{t("Are you sure you want to delete this news item?")}</div>
                    {deleteItem.title ? <div className="mt-2"><strong>{deleteItem.title}</strong></div> : null}
                    {error ? <div className="text-danger mt-2">{error}</div> : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteItem({ id: null, title: "" });
                    }} disabled={deleting}>
                        {t("Cancel")}
                    </Button>
                    <Button variant="danger" onClick={() => handleDelete(deleteItem.id)} disabled={deleting}>
                        {deleting ? t("Deleting...") : t("Delete")}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Separate modal used for Add and Edit */}
            <Modal show={showEditor} onHide={() => setShowEditor(false)} size="lg" centered autoFocus={false} enforceFocus={false}>
                <Modal.Header closeButton>
                    <Modal.Title>{t(editorMode === "edit" ? "Edit News" : "Add News")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-2" controlId="newsTitle">
                            <Form.Label>{t("Title")}</Form.Label>
                            <Form.Control
                                type="text"
                                value={editItem.title}
                                onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
                                placeholder={t("Enter title")}
                                autoFocus
                            />
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="newsContent">
                            <Form.Label>{t("Content")}</Form.Label>
                            <div>
                                <CKEditor
                                    editor={Editor}
                                    data={editItem.content}
                                    onReady={(editor) => {
                                        // editor ready
                                    }}
                                    onChange={(event, editor) => {
                                        const data = editor.getData();
                                        setEditItem({ ...editItem, content: data });
                                    }}
                                />
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-2" controlId="newsPublished">
                            <Form.Check
                                type="checkbox"
                                label={t("Published")}
                                checked={editItem.isPublished}
                                onChange={(e) => setEditItem({ ...editItem, isPublished: e.target.checked })}
                            />
                        </Form.Group>
                        {error ? <div className="text-danger mb-2">{error}</div> : null}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? t("Saving...") : t("Save")}
                    </Button>
                    <Button variant="secondary" onClick={() => {
                        // reset to create mode on cancel to avoid stale state
                        setShowEditor(false);
                        setEditItem({
                            id: null,
                            title: "",
                            content: "",
                            isPublished: true
                        });
                        setEditorMode("add");
                    }} disabled={saving}>
                        {t("Cancel")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default MemberNewsEditor;